/**
 * Host Me - Currency Formatting Utilities
 *
 * Per decision #57: separate from `dateFormatter.js` — single-purpose modules.
 *
 * Default currency is SAR. Stage 1+ commercials snapshot includes
 * `currency: 'SAR'` and `vatRate: 15` so callers can format any commercials
 * subdoc consistently.
 */

const SAR_SUFFIX = 'ر.س';

/**
 * Format a number as a SAR amount with 2 decimal places.
 *   formatSAR(750) → "750.00 ر.س"
 *   formatSAR(0)   → "0.00 ر.س"
 *   formatSAR(null) → "—"
 */
export const formatSAR = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)} ${SAR_SUFFIX}`;
};

/**
 * Compact form (no decimals when whole number).
 *   formatSARCompact(750)    → "750 ر.س"
 *   formatSARCompact(750.50) → "750.50 ر.س"
 */
export const formatSARCompact = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(n)) return '—';
  const isWhole = Math.abs(n - Math.round(n)) < 0.005;
  return `${isWhole ? Math.round(n) : n.toFixed(2)} ${SAR_SUFFIX}`;
};

/**
 * Reporting form: thousands-grouped, up to 2 decimals, Western digits.
 *   formatSARGrouped(125400.4) → "125,400.4 ر.س"
 *   formatSARGrouped(0.56)     → "0.56 ر.س"
 *   formatSARGrouped(0)        → "0 ر.س"
 *
 * Matches the /finance dashboard's number rule exactly (`toLocaleString('en-US',
 * { maximumFractionDigits: 2 })` + the suffix), because the owner report has to
 * reconcile against that dashboard for the same owner and month.
 *
 * ⚠️ Decimals are kept deliberately, NOT rounded to whole riyals. RevPAR and ADR
 * are routinely small: a real RevPAR of 0.56 rendered as "1 ر.س" is a different
 * number, and rounding it away is the kind of error a frozen owner-facing document
 * must not carry. Prose rounds and figures do not — the backend's Arabic
 * commentary (`money()` in `be/services/ownerReport/insights.js`) says
 * "1,936 ر.س" inside a sentence while the card beside it shows "1,936.41 ر.س".
 * That is ordinary reporting register, not a discrepancy.
 *
 * Western digits are deliberate: every figure on /finance goes through
 * `toLocaleString('en-US')`, so an Arabic-Indic report would not visually
 * reconcile with the dashboard beside it. Dates are the other convention — those
 * stay Arabic-Indic via `dateFormatter.js`.
 */
export const formatSARGrouped = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${SAR_SUFFIX}`;
};
